/**
 * Shared RestaurantSelector component for upload forms
 * Handles restaurant selection logic for both photo and CSV uploads
 */

import React, { useState, useEffect } from 'react';
import { Select, TextInput, Textarea, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import api from '../../services/api';

export interface RestaurantOption {
  value: string;
  label: string;
}

export interface RestaurantSelectorProps {
  selectedRestaurant: string | null;
  onRestaurantChange: (value: string | null) => void;
  newRestaurantName: string;
  onNewRestaurantNameChange: (value: string) => void;
  adminNotes: string;
  onAdminNotesChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  loading: boolean;
  onRestaurantsChange?: (restaurants: RestaurantOption[]) => void;
}

/**
 * RestaurantSelector component for handling restaurant selection and form fields
 * Provides consistent restaurant selection UI across upload pages
 */
export const RestaurantSelector: React.FC<RestaurantSelectorProps> = ({
  selectedRestaurant,
  onRestaurantChange,
  newRestaurantName,
  onNewRestaurantNameChange,
  adminNotes,
  onAdminNotesChange,
  description,
  onDescriptionChange,
  loading,
  onRestaurantsChange
}) => {
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);
  const showNewRestaurant = selectedRestaurant === 'other';

  // Fetch restaurants for the dropdown
  // Only fetch once on component mount to avoid unnecessary API calls during upload
  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const response = await api.get('/restaurants/');
        
        // Handle paginated response format: { data: [...], total, page, pageSize }
        // or direct array format for backwards compatibility
        const restaurantsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
        
        const formattedRestaurants = restaurantsData
          .filter((restaurant: any) => restaurant && restaurant.id && restaurant.name)
          .map((restaurant: any) => ({
            value: restaurant.id,
            label: restaurant.name || 'Unnamed Restaurant',
          }));
        
        // Add 'other' option and set it as default
        const otherOption = { value: 'other', label: 'Other' };
        const allRestaurants = [otherOption, ...formattedRestaurants];
        setRestaurants(allRestaurants);
        onRestaurantChange('other');
        
        // Notify parent component of restaurants
        onRestaurantsChange?.(allRestaurants);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to fetch restaurants',
          color: 'red',
        });
      }
    };

    fetchRestaurants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only fetch once on mount

  const handleRestaurantChange = (value: string | null) => {
    onRestaurantChange(value);
  };

  return (
    <Stack gap="md">
      <Select
        label="Select Restaurant"
        placeholder="Choose a restaurant"
        data={restaurants}
        value={selectedRestaurant}
        onChange={handleRestaurantChange}
        disabled={loading}
      />
      
      {showNewRestaurant && (
        <>
          <TextInput
            label="Restaurant Name"
            placeholder="Enter restaurant name"
            value={newRestaurantName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onNewRestaurantNameChange(event.currentTarget.value)
            }
            disabled={loading}
            required
          />
          <Textarea
            label="Restaurant description"
            description="Helps the AI interpret your menu. Mention cuisine or restaurant type, vibe (casual, fine dining, family-friendly), region or style, and anything distinctive."
            placeholder="e.g. Neapolitan pizzeria with a relaxed, neighborhood vibe; wood-fired oven, Italian wine list, kid-friendly early evenings"
            minRows={3}
            value={description}
            onChange={(event) => onDescriptionChange(event.currentTarget.value)}
            disabled={loading}
          />
          {/* Menu admin notes are in the "Menu settings" section below (same as Edit menu) */}
        </>
      )}
    </Stack>
  );
};

export default RestaurantSelector;
